import { Link, Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    LayoutDashboard,
    User,
    CreditCard,
    Receipt,
    CalendarDays,
    HeartPulse,
    Droplets,
    Smile,
    Trophy,
    LogOut,
    Menu,
    X,
    Store,
    ShoppingCart,
    Package,
    ChevronDown,
    Bell,
    Wrench,
    Timer,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const mainItems = [
    { to: '/mi-cuenta', icon: LayoutDashboard, label: 'Resumen', end: true },
    { to: '/mi-cuenta/tarjeta', icon: CreditCard, label: 'Mi Tarjeta' },
];

const classesItems = [
    { to: '/mi-cuenta/clases', icon: CalendarDays, label: 'Mis Clases' },
    { to: '/mi-cuenta/satisfaccion', icon: Smile, label: 'Satisfacción' },
    { to: '/mi-cuenta/rankings', icon: Trophy, label: 'Rankings' },
];

const paymentsItems = [
    { to: '/mi-cuenta/pagos', icon: Receipt, label: 'Mis Pagos' },
];

const healthItems = [
    { to: '/mi-cuenta/seguimiento', icon: HeartPulse, label: 'Seguimiento' },
    { to: '/mi-cuenta/hidratacion', icon: Droplets, label: 'Hidratación' },
];

const toolsItems = [
    { to: '/mi-cuenta/timer', icon: Timer, label: 'Timer' },
];

const profileItems = [
    { to: '/mi-cuenta/perfil', icon: User, label: 'Mi Perfil' },
];

const settingsItems = [
    { to: '/mi-cuenta/notificaciones', icon: Bell, label: 'Notificaciones' },
];

const storeItems = [
    { to: '/mi-cuenta/tienda', icon: ShoppingCart, label: 'Productos' },
    { to: '/mi-cuenta/mis-pedidos', icon: Package, label: 'Mis Pedidos' },
];

export default function CustomerLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [classesOpen, setClassesOpen] = useState(() =>
        location.pathname.startsWith('/mi-cuenta/clases') ||
        location.pathname.startsWith('/mi-cuenta/satisfaccion') ||
        location.pathname.startsWith('/mi-cuenta/rankings')
    );
    const [paymentsOpen, setPaymentsOpen] = useState(() =>
        location.pathname.startsWith('/mi-cuenta/pagos')
    );
    const [healthOpen, setHealthOpen] = useState(() =>
        location.pathname.startsWith('/mi-cuenta/seguimiento') ||
        location.pathname.startsWith('/mi-cuenta/hidratacion')
    );
    const [storeOpen, setStoreOpen] = useState(() =>
        location.pathname.startsWith('/mi-cuenta/tienda') ||
        location.pathname.startsWith('/mi-cuenta/mis-pedidos')
    );
    const [toolsOpen, setToolsOpen] = useState(() =>
        location.pathname.startsWith('/mi-cuenta/timer')
    );
    const [storeEnabled, setStoreEnabled] = useState(false);

    useEffect(() => {
        const fetchGyms = async () => {
            try {
                const res = await api.get('/customer/store/my-gyms');
                const gyms = res.data.data || [];
                setStoreEnabled(gyms.some(g => g.store_enabled));
            } catch {
                setStoreEnabled(false);
            }
        };
        fetchGyms();
    }, []);

    useEffect(() => {
        if (
            location.pathname.startsWith('/mi-cuenta/clases') ||
            location.pathname.startsWith('/mi-cuenta/satisfaccion') ||
            location.pathname.startsWith('/mi-cuenta/rankings')
        ) {
            setClassesOpen(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (location.pathname.startsWith('/mi-cuenta/pagos')) {
            setPaymentsOpen(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (
            location.pathname.startsWith('/mi-cuenta/seguimiento') ||
            location.pathname.startsWith('/mi-cuenta/hidratacion')
        ) {
            setHealthOpen(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (
            location.pathname.startsWith('/mi-cuenta/tienda') ||
            location.pathname.startsWith('/mi-cuenta/mis-pedidos')
        ) {
            setStoreOpen(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (location.pathname.startsWith('/mi-cuenta/timer')) {
            setToolsOpen(true);
        }
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isGroupActive = (prefixes) => prefixes.some(p => location.pathname.startsWith(p));

    return (
        <div className="min-h-screen flex bg-black">
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            <aside className={cn(
                "fixed inset-y-0 left-0 z-50 w-64 bg-black border-r border-hairline-dark transform transition-transform lg:translate-x-0 lg:static lg:z-auto",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4">
                        <h1 className="text-xl font-bold text-white uppercase tracking-wide">
                            <Link to="/" onClick={() => setSidebarOpen(false)}>GYMS.ar</Link>
                        </h1>
                        <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                    <div className="px-4 pb-2">
                        <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                    </div>
                    <Separator />
                    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                        {mainItems.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-white hover:bg-white/5"
                                )}
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                            </NavLink>
                        ))}

                        {/* Clases Group */}
                        <div className="mt-1">
                            <button
                                type="button"
                                onClick={() => setClassesOpen(o => !o)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isGroupActive(['/mi-cuenta/clases', '/mi-cuenta/satisfaccion', '/mi-cuenta/rankings'])
                                        ? "text-white"
                                        : "text-white/60 hover:bg-white/5"
                                )}
                            >
                                <CalendarDays className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider text-left">Clases</span>
                                <ChevronDown className={cn("h-4 w-4 transition-transform", classesOpen ? "rotate-180" : "")} />
                            </button>
                            {classesOpen && (
                                <div className="mt-1 space-y-1 pl-4">
                                    {classesItems.map(item => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) => cn(
                                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                isActive
                                                    ? "bg-white/10 text-white"
                                                    : "text-white/60 hover:bg-white/5"
                                            )}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Pagos Group */}
                        <div className="mt-1">
                            <button
                                type="button"
                                onClick={() => setPaymentsOpen(o => !o)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isGroupActive(['/mi-cuenta/pagos'])
                                        ? "text-white"
                                        : "text-white/60 hover:bg-white/5"
                                )}
                            >
                                <Receipt className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider text-left">Pagos</span>
                                <ChevronDown className={cn("h-4 w-4 transition-transform", paymentsOpen ? "rotate-180" : "")} />
                            </button>
                            {paymentsOpen && (
                                <div className="mt-1 space-y-1 pl-4">
                                    {paymentsItems.map(item => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) => cn(
                                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                isActive
                                                    ? "bg-white/10 text-white"
                                                    : "text-white/60 hover:bg-white/5"
                                            )}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Salud Group */}
                        <div className="mt-1">
                            <button
                                type="button"
                                onClick={() => setHealthOpen(o => !o)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isGroupActive(['/mi-cuenta/seguimiento', '/mi-cuenta/hidratacion'])
                                        ? "text-white"
                                        : "text-white/60 hover:bg-white/5"
                                )}
                            >
                                <HeartPulse className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider text-left">Salud</span>
                                <ChevronDown className={cn("h-4 w-4 transition-transform", healthOpen ? "rotate-180" : "")} />
                            </button>
                            {healthOpen && (
                                <div className="mt-1 space-y-1 pl-4">
                                    {healthItems.map(item => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) => cn(
                                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                isActive
                                                    ? "bg-white/10 text-white"
                                                    : "text-white/60 hover:bg-white/5"
                                            )}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Herramientas Group */}
                        <div className="mt-1">
                            <button
                                type="button"
                                onClick={() => setToolsOpen(o => !o)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isGroupActive(['/mi-cuenta/timer'])
                                        ? "text-white"
                                        : "text-white/60 hover:bg-white/5"
                                )}
                            >
                                <Wrench className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider text-left">Herramientas</span>
                                <ChevronDown className={cn("h-4 w-4 transition-transform", toolsOpen ? "rotate-180" : "")} />
                            </button>
                            {toolsOpen && (
                                <div className="mt-1 space-y-1 pl-4">
                                    {toolsItems.map(item => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setSidebarOpen(false)}
                                            className={({ isActive }) => cn(
                                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                isActive
                                                    ? "bg-white/10 text-white"
                                                    : "text-white/60 hover:bg-white/5"
                                            )}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {profileItems.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-white hover:bg-white/5"
                                )}
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                            </NavLink>
                        ))}

                        <Separator className="my-2" />

                        {settingsItems.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-white/60 hover:bg-white/5"
                                )}
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                            </NavLink>
                        ))}

                        {storeEnabled && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={() => setStoreOpen(o => !o)}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-white/60 hover:bg-white/5"
                                    )}
                                >
                                    <Store className="h-4 w-4" />
                                    <span className="flex-1 uppercase tracking-wider text-left">Tienda</span>
                                    <ChevronDown className={cn("h-4 w-4 transition-transform", storeOpen ? "rotate-180" : "")} />
                                </button>
                                {storeOpen && (
                                    <div className="mt-1 space-y-1 pl-4">
                                        {storeItems.map(item => (
                                            <NavLink
                                                key={item.to}
                                                to={item.to}
                                                end={item.end}
                                                onClick={() => setSidebarOpen(false)}
                                                className={({ isActive }) => cn(
                                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                    isActive
                                                        ? "bg-white/10 text-white"
                                                        : "text-white/60 hover:bg-white/5"
                                                )}
                                            >
                                                <item.icon className="h-4 w-4" />
                                                <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                    <Separator />
                    <div className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                                <p className="text-xs text-white/60 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleLogout}>
                            <LogOut className="h-4 w-4 mr-2" />
                            Cerrar sesión
                        </Button>
                    </div>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-30 bg-black border-b border-hairline-dark px-4 py-3 flex items-center justify-between lg:hidden">
                    <div className="flex items-center">
                        <Button variant="outline" size="icon" onClick={() => setSidebarOpen(true)}>
                            <Menu className="h-5 w-5" />
                        </Button>
                        <h1 className="ml-3 text-lg font-bold uppercase tracking-wide text-white">
                            <Link to="/">GYMS.ar</Link>
                        </h1>
                    </div>
                    <span className="text-sm font-medium text-white">{user?.name}</span>
                </header>
                <main className="flex-1 p-4 md:p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
